import { Hash, Plus, Users, X } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout, EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { linePurposeText } from "@/lib/line";
import { useAddTeamLine, useRemoveTeamLine, useTeamFromLine, useTeams } from "@/lib/queries";
import { addedByHand, leadNames, onLineCount, teamsOnCampaign, type TeamOnLine } from "@/lib/teams";
import type { Campaign, QueueMember } from "@/lib/types";

/**
 * The teams on a campaign's lines (platform ADR-185), on its Staffing tab above the table of every
 * rep. Everyone in a team who has a phone is on the team's line, and somebody added to the team
 * later joins it too. Putting a team on a line, or taking it off, is an admin's, as staffing by hand
 * is; a supervisor reads it.
 *
 * A core-api older than teams answers `/teams` with 404: the card is left out, and the Staffing
 * table below it reads as it always has.
 */
export function CampaignTeams({ campaign, canManage }: { campaign: Campaign; canManage: boolean }) {
  const toast = useToast();
  const teams = useTeams();
  const remove = useRemoveTeamLine();
  const [adding, setAdding] = React.useState(false);
  const [removing, setRemoving] = React.useState<TeamOnLine | null>(null);

  if (teams.error instanceof ApiError && teams.error.status === 404) return null;
  const rows = teamsOnCampaign(teams.data ?? [], campaign.id);

  const addButton = canManage && (
    <Button variant="outline" size="sm" onClick={() => setAdding(true)} disabled={campaign.lines.length === 0}>
      <Plus />
      Add a team
    </Button>
  );

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="Teams"
        description="The teams on this campaign's lines. Everyone in a team is on its line, and somebody added to the team later joins it too."
        action={addButton}
      />
      {teams.isPending ? (
        <TableSkeleton cols={4} />
      ) : teams.error ? (
        <ErrorState
          error={teams.error}
          title="Could not load the teams"
          action={
            <Button variant="outline" onClick={() => void teams.refetch()}>
              Try again
            </Button>
          }
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No team staffs this campaign yet"
          description="Put a team on one of its lines and everyone in it takes that line's calls. Every rep on the campaign's lines is listed below."
          action={addButton || undefined}
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Team</TableHead>
              <TableHead>Line</TableHead>
              <TableHead>On the line</TableHead>
              <TableHead>Lead</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(({ team, line }) => (
              <TableRow key={`${team.id}:${line.queue_id}`}>
                <TableCell>
                  <Link
                    to={`/teams/${team.id}`}
                    className="inline-flex items-center gap-1.5 font-medium text-text hover:text-accent-11"
                  >
                    <Hash className="size-3.5 text-text-muted" aria-hidden />
                    {team.name}
                  </Link>
                </TableCell>
                <TableCell>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="font-mono text-xs">{line.queue_name}</span>
                    <Badge tone="neutral" size="sm">
                      {linePurposeText(line.purpose).label}
                    </Badge>
                  </span>
                </TableCell>
                <TableCell className="text-sm text-text">{onLineCount(team)}</TableCell>
                <TableCell className="text-xs text-text-muted">{leadNames(team) ?? "—"}</TableCell>
                <TableCell>
                  {canManage && (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Take ${team.name} off ${line.queue_name}`}
                      onClick={() => setRemoving({ team, line })}
                    >
                      <X />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AddTeamDialog open={adding} onOpenChange={setAdding} campaign={campaign} />

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title="Take this team off the line?"
        description={
          <>
            Everyone in <span className="font-medium">{removing?.team.name}</span> comes off{" "}
            <span className="font-mono">{removing?.line.queue_name}</span>, except anybody another team
            puts there or somebody added on their own.
          </>
        }
        confirmLabel="Take the team off"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!removing) return;
          try {
            await remove.mutateAsync({ teamId: removing.team.id, lineId: removing.line.queue_id });
            toast.success("Team taken off the line", `${removing.team.name} · ${removing.line.queue_name}`);
            setRemoving(null);
          } catch (e) {
            toast.error("Could not take the team off", e instanceof ApiError ? e.message : "Please try again.");
          }
        }}
      />
    </Card>
  );
}

function AddTeamDialog({
  open, onOpenChange, campaign,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign;
}) {
  const toast = useToast();
  const teams = useTeams({ enabled: open });
  const add = useAddTeamLine();
  const [teamId, setTeamId] = React.useState<number | null>(null);
  const [lineId, setLineId] = React.useState<number | null>(null);
  const [formError, setFormError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setFormError(null);
    setTeamId(null);
    setLineId(campaign.queue_id ?? campaign.lines[0]?.id ?? null);
  }, [open, campaign.queue_id, campaign.lines]);

  const team = teams.data?.find((t) => t.id === teamId);
  const line = campaign.lines.find((l) => l.id === lineId);
  const already = team !== undefined && team.lines.some((l) => l.queue_id === lineId);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    if (!team || !line) {
      setFormError("Choose a team and a line.");
      return;
    }
    try {
      await add.mutateAsync({ teamId: team.id, lineId: line.id });
      toast.success("Team added", `${team.name} on ${line.name}`);
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not add the team.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={teamId !== null} />
        <DialogHeader>
          <DialogTitle>Add a team to {campaign.name}</DialogTitle>
          <DialogDescription>
            Everyone in the team with a phone goes on the line, and is offered its calls once they are
            Ready. Nobody's skills change.
          </DialogDescription>
        </DialogHeader>
        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}
            <Field
              label="Team"
              htmlFor="campaign-team"
              required
              error={already ? "Already on this line." : undefined}
              hint={
                team
                  ? `${onLineCount(team)} of its ${team.members.length} people have a phone and go on the line.`
                  : undefined
              }
            >
              <Select value={teamId?.toString() ?? ""} onValueChange={(v) => setTeamId(Number(v))}>
                <SelectTrigger id="campaign-team">
                  <SelectValue placeholder={teams.isPending ? "Loading teams…" : "Choose a team"} />
                </SelectTrigger>
                <SelectContent>
                  {(teams.data ?? []).map((t) => (
                    <SelectItem key={t.id} value={String(t.id)} description={`${t.members.length} people`}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            {(teams.data ?? []).length === 0 && !teams.isPending && (
              <Callout tone="info">
                There are no teams yet. Make one on{" "}
                <Link to="/teams" className="underline underline-offset-4">
                  Teams
                </Link>
                .
              </Callout>
            )}
            <Field label="Line" htmlFor="campaign-team-line" required hint={linePurposeText(line?.purpose ?? null).description}>
              <Select value={lineId?.toString() ?? ""} onValueChange={(v) => setLineId(Number(v))}>
                <SelectTrigger id="campaign-team-line">
                  <SelectValue placeholder="Choose a line" />
                </SelectTrigger>
                <SelectContent>
                  {campaign.lines.map((l) => (
                    <SelectItem key={l.id} value={String(l.id)} description={linePurposeText(l.purpose).label}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={add.isPending} disabled={already}>
              Add the team
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

/**
 * "Make a team from reps added by hand": the reps added to one of the campaign's lines by hand
 * become a team, and the team takes the line over. They stay on it, as the team's.
 */
export function TeamFromLineDialog({
  open, onOpenChange, campaign, members,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign;
  members: readonly QueueMember[];
}) {
  const toast = useToast();
  const make = useTeamFromLine();
  const byHand = addedByHand(members);
  const lineIds = campaign.lines.filter((l) => byHand.some((m) => m.queue_id === l.id)).map((l) => l.id);
  const [lineId, setLineId] = React.useState<number | null>(null);
  const [name, setName] = React.useState("");
  /** Typed by the admin, so a change of line no longer renames it. */
  const [named, setNamed] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);
  const suggested = (id: number | null) => {
    const l = campaign.lines.find((x) => x.id === id);
    return l ? `${campaign.name} · ${linePurposeText(l.purpose).label}` : "";
  };

  React.useEffect(() => {
    if (!open) return;
    setFormError(null);
    setNamed(false);
    const first = lineIds[0] ?? null;
    setLineId(first);
    setName(suggested(first));
    // Only on opening: the members refetch while it is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pickLine = (id: number) => {
    setLineId(id);
    if (!named) setName(suggested(id));
  };

  const line = campaign.lines.find((l) => l.id === lineId);
  const people = byHand.filter((m) => m.queue_id === lineId && m.app_user_id !== null);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    if (!line || !name.trim()) {
      setFormError("Choose a line and name the team.");
      return;
    }
    try {
      const team = await make.mutateAsync({ lineId: line.id, name: name.trim() });
      toast.success("Team made", `${team.name}: ${team.members.length} people on ${line.name}`);
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not make the team.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DialogHeader>
          <DialogTitle>Make a team from reps added by hand</DialogTitle>
          <DialogDescription>
            The reps added to the line on their own become a team, and the team holds the line. Nobody
            comes off it, and nobody's skills change.
          </DialogDescription>
        </DialogHeader>
        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}
            <Field label="Line" htmlFor="team-from-line" required>
              <Select value={lineId?.toString() ?? ""} onValueChange={(v) => pickLine(Number(v))}>
                <SelectTrigger id="team-from-line">
                  <SelectValue placeholder="Choose a line" />
                </SelectTrigger>
                <SelectContent>
                  {campaign.lines
                    .filter((l) => lineIds.includes(l.id))
                    .map((l) => (
                      <SelectItem key={l.id} value={String(l.id)} description={linePurposeText(l.purpose).label}>
                        {l.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Team name" htmlFor="team-from-line-name" required>
              <Input
                id="team-from-line-name"
                value={name}
                maxLength={80}
                onChange={(e) => {
                  setName(e.target.value);
                  setNamed(true);
                }}
              />
            </Field>
            <div className="rounded-md border border-border bg-surface-sunken px-3 py-2 text-xs text-text-muted">
              {people.length === 0
                ? "Nobody on this line was added on their own."
                : `${people.length} ${people.length === 1 ? "person joins" : "people join"} the team: ${people
                    .map((m) => m.display_name ?? m.user)
                    .join(", ")}.`}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={make.isPending} disabled={people.length === 0}>
              Make the team
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
